import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { RouterModule } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { AppNotificationsService } from '@app/shared/services/app-notifications.service';
import { ApiCallService } from '@app/services/api-call.service';
import { SocketService } from '@app/socket.service';
import { UtilService } from '../../app/shared/services/util.service';
import moment from 'moment';
import { FlagService } from "../shared/services/notification.service";
import { forkJoin, from } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { KeyValue } from '@angular/common';
import { Router } from '@angular/router';

@Component({
    imports: [CommonModule, FormsModule, SharedModule, RouterModule],
    selector: 'app-notifications',
    templateUrl: './all-notifications.component.html',
    styleUrls: ['./all-notifications.component.css'],
    encapsulation: ViewEncapsulation.None
})
export class NotificationsComponent implements OnInit {
  private readonly hiddenNotificationMenus = new Set(['project']);
  // LEFT panel variables
  distance = 2;
  throttle = 250;
  page = 1;
  size = 150;
  hoverActive = false;
  notifications: any;
  paginatedNotifications: any = {};
  itemIndex = -1;
  noNotificationsMessage = '';
  itemKey = '';
  checked = false;
  notificationsError = false;
  allNotifications: any[] = [];
  isMenuOpen = true; // Always open for left panel
  notificationSelected = false;
  // RIGHT panel variables
  selectedNotification: any = null;
  selectedType = '';
  pageSize = 5; // Show 5 notifications per page
  length = 0;
  notificationsLoaded = false;
  typesLoaded = false;
  isSearching = false;
  isLoading = false;
  isloading = false;
  searchInput = '';
  isSearchInput = false;
  showResults: any[] = [];
  tags: any = {};
  menuEventMap = {};
  perEventCounts: any = {};
  totalNotificationCount = 0;
  totalAllnotification = 0;
  private paginationStateByTab: Record<string, { currentPage: number; totalItems: number; totalPages: number }> = {};

  // Pagination properties
  currentPage: number = 1;
  totalItems: number = 0;
  totalPages: number = 0;

  notificationFields = [
    { label: 'Title', key: 'title' },
    { label: 'Message', key: 'message' },
    { label: 'Type', key: 'type' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Status', key: 'status' }
  ];

  constructor(
    private flagService: FlagService,
    private appNotificationsService: AppNotificationsService,
    private apiCallService: ApiCallService,
    private util: UtilService,
    private socketService: SocketService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }
  ngOnInit(): void {
    this.isloading = true;

    this.getAllNotifications(this.page, this.size);
    this.socketService.onNewNotification().subscribe((msg: any) => {
      this.getAllNotifications(1, this.size);
      this.refreshCurrentNotificationView();
    });
    this.flagService.flagFromB.subscribe(flag => {
      if (flag) {
        this.refreshCurrentNotificationView();
      }
    });

    this.fetchNotifications();
   
    this.getNotificationTypes();
  }

  sendToB() {
    this.flagService.sendFlagFromA(true);
  }

  private getEventIdsForType(type: string | null): number[] | undefined {
    if (!type || type === 'All') {
      return undefined;
    }

    const eventIds = this.menuEventMap[type];
    return Array.isArray(eventIds) ? eventIds : undefined;
  }

  private refreshCurrentNotificationView(): void {
    this.notificationSelected = false;
    this.selectedNotification = null;
    this.currentPage = 1;
    this.currentPageIndex = 0;

    const eventIds = this.getEventIdsForType(this.selectedType || 'All');
    this.fetchNotifications(this.currentPageIndex, this.size, eventIds);
  }

  // 🔹 PAGINATION METHODS
  private getPaginationStateKey(type: string | null = null): string {
    return `${type || 'All'}::${this.checked ? 'unread' : 'all'}`;
  }

  private persistPaginationState(key: string): void {
    this.paginationStateByTab[key] = {
      currentPage: this.currentPage,
      totalItems: this.totalItems,
      totalPages: this.totalPages,
    };
  }

  // Pagination methods
  initializePagination() {
    if (this.notifications) {
      const paginationKey = this.getPaginationStateKey(this.selectedType || 'All');
      const storedState = this.paginationStateByTab[paginationKey];

      this.totalItems = this.getTotalNotificationCount();
      this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
      this.currentPage = storedState?.currentPage ? Math.min(storedState.currentPage, this.totalPages) : 1;
      this.currentPageIndex = this.currentPage - 1;
      this.updatePaginatedNotifications();
      this.persistPaginationState(paginationKey);
    }
  }

  getTotalNotificationCount(): number {
    let count = 0;
    if (this.notifications) {
      for (const key in this.notifications) {
        if (this.notifications.hasOwnProperty(key)) {
          count += this.notifications[key].length;
        }
      }
    }
    return count;
  }

  updatePaginatedNotifications() {
    if (!this.notifications) {
      this.paginatedNotifications = {};
      return;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    let currentIndex = 0;
    this.paginatedNotifications = {};

    // Iterate through notifications and add to paginated result
    for (const dateKey in this.notifications) {
      if (this.notifications.hasOwnProperty(dateKey)) {
        const notificationsForDate = this.notifications[dateKey];

        for (const notification of notificationsForDate) {
          if (currentIndex >= startIndex && currentIndex < endIndex) {
            if (!this.paginatedNotifications[dateKey]) {
              this.paginatedNotifications[dateKey] = [];
            }
            this.paginatedNotifications[dateKey].push(notification);
          }
          currentIndex++;

          if (currentIndex >= endIndex) {
            break;
          }
        }

        if (currentIndex >= endIndex) {
          break;
        }
      }
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedNotifications();
      this.currentPageIndex = this.currentPage - 1;
      this.persistPaginationState(this.getPaginationStateKey(this.selectedType || 'All'));
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedNotifications();
      this.currentPageIndex = this.currentPage - 1;
      this.persistPaginationState(this.getPaginationStateKey(this.selectedType || 'All'));
    }
  }

  getEndIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }


  onScroll(): void {
    if (this.isLoading || !this.isMenuOpen) return;
    this.getAllNotifications(++this.page, this.size);
  }

  handle_no_url(event: Event) {
    event.stopPropagation();
  }

  printInitial(username: string): string {
    console.log('Avatar Username:', username);
    const [first, last] = (username || '').split(' ');
    return `${first.charAt(0).toUpperCase()}${last ? last.charAt(0).toUpperCase() : ''}`;
  }

  getAllNotifications(page: number, size: number) {
    this.appNotificationsService.getNotifications(page, size).subscribe(
      (data: any) => {
        const notifications = data.body.responseBody;
        this.allNotifications = notifications;

        const list = this.checked
          ? this.allNotifications.filter((item: any) => !item.is_read)
          : this.allNotifications;

        this.notifications = this.util.getFormattedNotifications(list);
        this.initializePagination(); // Initialize pagination after notifications are loaded
        this.cdr.detectChanges();
        this.notificationsLoaded = true;
        this.checkLoading();
      },
      (err: any) => {
        this.notificationsLoaded = true;
        this.checkLoading();
        this.notificationsError = true;
      }
    );
  }

  onInputChange() {
    this.checked = !this.checked;
    console.log(this.allNotifications)
    if (this.checked) {
      const list = this.allNotifications.filter((item: any) => !item.is_read);
      this.notifications = this.util.getFormattedNotifications(list);
    } else {
      this.notifications = this.util.getFormattedNotifications(
        this.allNotifications
      );
    }
    this.initializePagination(); // Update pagination when filter changes
  }

  showUnreadOnly: boolean = false;

  toggleUnreadOnly() {
    this.checked = !this.checked;
    console.log(this.allNotifications, "unreadonly");
    const list = this.checked
      ? this.allNotifications.filter((item: any) => !item.is_read)
      : this.allNotifications;

    this.notifications = this.util.getFormattedNotifications(list);
    this.initializePagination(); // Update pagination when filter changes
  }

  getFormattedTime(date: any): string {
    if (!date) return '';

    const notificationTime = moment.utc(date).local();
    const now = moment();
    const diffInMinutes = now.diff(notificationTime, 'minutes');
    const diffInHours = now.diff(notificationTime, 'hours');
    const diffInDays = now.diff(notificationTime, 'days');

    // Show relative time for recent notifications
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      // For older notifications, show actual date
      return notificationTime.format('MMM DD, HH:mm A');
    }
  }

  getCleanMessage(message: string): string {
    if (!message) return '';

    // Remove common date/time patterns from the message
    const dateTimePatterns = [
      /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/g, // YYYY-MM-DD HH:mm:ss
      /\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}/g, // DD/MM/YYYY HH:mm:ss
      /\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}:\d{2} [AP]M/g, // MM/DD/YYYY HH:mm:ss AM/PM
      /\d{1,2}\/\d{1,2}\/\d{4}/g, // MM/DD/YYYY or DD/MM/YYYY
      /\d{4}-\d{2}-\d{2}/g, // YYYY-MM-DD
      /\d{1,2}:\d{2}:\d{2} [AP]M/g, // HH:mm:ss AM/PM
      /\d{1,2}:\d{2} [AP]M/g, // HH:mm AM/PM
      /at \d{1,2}:\d{2}:\d{2}/g, // "at HH:mm:ss"
      /on \d{1,2}\/\d{1,2}\/\d{4}/g, // "on MM/DD/YYYY"
    ];

    let cleanMessage = message;

    // Remove all date/time patterns
    dateTimePatterns.forEach(pattern => {
      cleanMessage = cleanMessage.replace(pattern, '').trim();
    });

    // Clean up any extra spaces or punctuation left after removal
    cleanMessage = cleanMessage
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/\s*\.\s*$/, '') // Remove trailing period with spaces
      .replace(/\s*,\s*$/, '') // Remove trailing comma with spaces
      .trim();

    return cleanMessage || message; // Return original if cleaning removes everything
  }

 orderOriginal = (
    a: KeyValue<number, string>,
    b: KeyValue<number, string>
  ): number => {
    return 0;
  };

  getNotificationHeading(notification: any): string {
    return notification?.title || '-';
  }

  getNotificationEventType(notification: any): string {
    const title = (notification?.title || '').toLowerCase();
    const actionType = (
      notification?.metadata?.action_type ||
      notification?.action_type ||
      ''
    ).toLowerCase();

    if (title.includes('mention') || actionType === 'mentioned') {
      return 'Mention';
    }

    if (title.includes('deleted') || actionType === 'deleted') {
      return 'Deletion';
    }

    if (
      title.includes('updated') ||
      title.includes('modified') ||
      actionType === 'updated' ||
      actionType === 'modified'
    ) {
      return 'Modification';
    }

    if (
      title.includes('created') ||
      title.includes('bulk upload') ||
      actionType === 'created'
    ) {
      return 'Creation';
    }

    return '-';
  }

  getNotificationCreatedBy(notification: any): string {
    return (
      notification?.metadata?.inputData?.full_name ||
      notification?.metadata?.full_name ||
      notification?.metadata?.actor_name ||
      notification?.sender ||
      notification?.metadata?.recipient_name ||
      '-'
    );
  }

 selectNotification(notification: any) {
  if (this.selectedNotification?.id === notification?.id) {
    this.selectedNotification = null;
    this.notificationSelected = false;
    return;
  }

  notification.metadata = {
    ...notification.metadata,
    eventType: this.getNotificationEventType(notification)
  };

  if (notification.message) {
    const messageParts = notification.message.split(' by ');
    notification.cleanedMessage = messageParts[0];
  } else {
    notification.cleanedMessage = '';
  }

  this.selectedNotification = notification;
  this.notificationSelected = true;

  if (!notification.is_read) {
    this.markAsRead(null, notification.id);
  }
}

  normalizeNotificationRoute(rawUrl: string): string {
    if (!rawUrl || typeof rawUrl !== 'string') {
      return '';
    }

    let normalized = rawUrl.trim();
    if (!normalized) {
      return '';
    }

    try {
      if (/^https?:\/\//i.test(normalized)) {
        const parsed = new URL(normalized);
        normalized = `${parsed.pathname || ''}${parsed.search || ''}${parsed.hash || ''}`;
      }
    } catch (_) {
      // Keep raw value when URL parsing fails
    }

    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }

    const legacyQuestionReviewMatch = normalized.match(/\/Library\/Questions\/QuestionUpdateReview\/(\d+)/i);
    if (legacyQuestionReviewMatch?.[1]) {
      return `/Library/QuestionPapers/QuestionPaperVersionReview/${legacyQuestionReviewMatch[1]}?reviewType=question`;
    }

    const [pathOnly, queryString] = normalized.split('?');
    const segments = pathOnly
      .split('/')
      .filter(Boolean)
      .map((segment, index) => {
        const key = segment.toLowerCase();
        if (index === 0 && key === 'library') return 'Library';
        if (index === 1 && key === 'questions') return 'Questions';
        if (index === 1 && key === 'questionpapers') return 'QuestionPapers';
        if (index === 2 && key === 'questionupdatereview') return 'QuestionUpdateReview';
        if (index === 2 && key === 'questionpaperversionreview') return 'QuestionPaperVersionReview';
        return segment;
      });

    const normalizedPath = `/${segments.join('/')}`;
    return queryString ? `${normalizedPath}?${queryString}` : normalizedPath;
  }

  openNotificationLink(event: Event, notification: any) {
    event.stopPropagation();
    const route = this.normalizeNotificationRoute(notification?.url);
    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  markAsRead(event: any, id?: number) {
    if (event) {
      event.stopPropagation();
    }
    this.appNotificationsService.markAsRead(id).subscribe(
      () => {
        if (id) {
          const notification = this.allNotifications.find((n: any) => n.id === id);
          if (notification) notification.is_read = true;
        } else {
          this.allNotifications.forEach((n: any) => (n.is_read = true));
        }

        const list = this.checked
          ? this.allNotifications.filter((item: any) => !item.is_read)
          : this.allNotifications;

        this.notifications = this.util.getFormattedNotifications(list);
        this.initializePagination(); // Update pagination after marking as read
        this.cdr.detectChanges();
      },
      (err: any) => {
        this.util.simpleDialog({
          title: 'Error',
          message: 'Failed to mark as read.'
        });
      }
    );
    this.sendToB();
  }

  getTime(date: any) {
    return moment.utc(date).format('YYYY-MM-DD HH:mm:ss');
  }

  // 🔹 RIGHT SIDE METHODS

onNotificationTypeClick(type: string | null): void {
  this.notificationSelected = false;
  this.selectedNotification = null;

  this.selectedType = type && type !== 'All' ? type : '';
  const paginationKey = this.getPaginationStateKey(this.selectedType || 'All');
  const storedState = this.paginationStateByTab[paginationKey];

  this.currentPage = storedState?.currentPage || 1;
  this.currentPageIndex = this.currentPage - 1;

  const eventIds = this.getEventIdsForType(type);

  console.log('Clicked Tab:', type);
  console.log('Event IDs:', eventIds);

  this.fetchNotifications(0, this.size, eventIds);
}
  getFormattedDateTime(date: any): string {
    if (!date) return '';

    const notificationTime = moment.utc(date).local();
    return notificationTime.format('MMM DD, HH:mm A');
  }
  getNotificationTypes(): void {
    this.isLoading = true;

    this.appNotificationsService.getAllNotificationEvents().subscribe(
      (data: any) => {
        const response = data.body.responseBody;
        console.log(response);

        // Build the event map and populate tags
        Object.keys(response).forEach((key) => {
          response[key].forEach((item: any) => {
            if (this.hiddenNotificationMenus.has((item.menu_name || '').toLowerCase())) {
              return;
            }

            if (!this.menuEventMap[item.menu_name]) {
              this.menuEventMap[item.menu_name] = [];
            }
            this.menuEventMap[item.menu_name].push(item.event_id);
          });
        });

        console.log(this.menuEventMap, "menu");

        // Instead of making count API calls, just store eventIds in tags
        Object.keys(this.menuEventMap).forEach(menuName => {
          this.tags[menuName] = this.menuEventMap[menuName]; // Directly store event IDs
        });

        this.isLoading = false;
        this.typesLoaded = true;
        this.cdr.detectChanges();
        this.checkLoading();
      },
      (err: any) => {
        this.isLoading = false;
        this.isSearching = false;
        this.typesLoaded = true;
        this.cdr.detectChanges();
        this.checkLoading();
      }
    );
  }

  getNotificationCountForMenu(menuName: string, eventIds: number[]): Observable<any> {
    return this.appNotificationsService.getNotificationCount({ event_ids: eventIds }).pipe(
      tap((data: any) => {
        const res = data.body.responseBody;
        this.tags[menuName] = res.total_count;
        this.perEventCounts[menuName] = res.per_event_count;
        this.cdr.markForCheck();
      })
    );
  }

  currentPageIndex = 0;

  fetchNotifications(pageIndex: number = 0, pageSize: number = 100, eventIds?: number[]): void {
    const page = pageIndex + 1;

    let request$ = eventIds && eventIds.length
      ? this.appNotificationsService.getNotificationsByEventIdsPaginated(eventIds, page, pageSize)
      : this.appNotificationsService.getNotifications(page, pageSize);
console.log('Fetching notifications for:', eventIds);
    request$.subscribe(
      (response) => {
        console.log('API Response:', response);
        const resBody = response.body.responseBody;
        let notifications = resBody?.data || resBody || [];
        console.log('Notifications Count:', notifications.length);
        notifications = notifications.sort((a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        this.showResults = [...notifications];
this.allNotifications = [...notifications];

const filteredNotifications = this.checked
  ? notifications.filter((n: any) => !n.is_read)
  : notifications;

this.notifications = this.util.getFormattedNotifications(
  filteredNotifications
);

console.log(
  'Loaded notifications:',
  filteredNotifications.length,
  'for',
  this.selectedType || 'All'
);

        this.initializePagination(); // Initialize pagination after fetching notifications

        this.totalNotificationCount =
          resBody?.total_count ||
          resBody?.[0]?.total_count ||
          notifications.length;

        if (eventIds && (!notifications || notifications.length === 0)) {
          this.noNotificationsMessage = 'No notifications for the selected menu';
        } else {
          this.noNotificationsMessage = '';
        }

        this.length = this.totalNotificationCount;
        this.notificationsLoaded = true;
        this.checkLoading();
        this.cdr.detectChanges();
        this.cdr.detectChanges();
      },
      () => {
        this.showResults = [];
        this.notificationsLoaded = true;
        this.checkLoading();
        this.cdr.markForCheck();
      }
    );
  }
private getNotificationActorName(notification: any): string {
  const metadata = notification?.metadata || {};

  return (
    metadata?.inputData?.full_name ||
    metadata?.full_name ||
    metadata?.actor_name ||
    metadata?.recipient_name ||
    notification?.sender ||
    ''
  );
}

  getNotificationAvatarText(notification: any): string {
    const actorName = this.getNotificationActorName(notification);
    if (actorName) {
      return this.printInitial(actorName);
    }

    return notification?.created_user_id === notification?.notifier_user_id ? 'S' : 'N';
  }

  getNotificationAvatarTooltip(notification: any): string {
    const actorName = this.getNotificationActorName(notification);
    if (actorName) {
      return actorName;
    }

    return notification?.created_user_id === notification?.notifier_user_id
      ? 'System Notification'
      : 'Notification';
  }

  checkLoading() {
    this.isloading = !(this.typesLoaded && this.notificationsLoaded);
    this.cdr.detectChanges();
  }

  onChangePage(pe: PageEvent) {
    this.pageSize = pe.pageSize;
    this.currentPageIndex = pe.pageIndex;
    this.currentPage = pe.pageIndex + 1;

    const eventIds = this.selectedType ? this.menuEventMap[this.selectedType] : undefined;
    this.fetchNotifications(this.currentPageIndex, this.size, eventIds);
    this.persistPaginationState(this.getPaginationStateKey(this.selectedType || 'All'));
  }

  search() {
    if (this.searchInput.length < 3) return;
    this.isSearching = true;
    // Implement API search logic here
  }
}
